import { API_CONFIG } from '@/config/api';
import { useAuthStore } from '@/stores/useAuthStore';
import type { ApiError, HttpClient, RequestOptions } from '@/types/api';

// Navigation is handled via custom events (auth:redirect-to-login, auth:session-expired)
// that the root App component listens for. This avoids full page reloads and preserves
// React Router state while keeping httpClient decoupled from router context.

class HttpClientImpl implements HttpClient {
  private baseUrl: string;

  constructor() {
    // Use environment-driven API URL (consistent with API_CONFIG)
    this.baseUrl = API_CONFIG.BASE_URL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {},
    isRetry = false, // Add retry flag to prevent infinite loops
  ): Promise<T> {
    const { timeout = API_CONFIG.TIMEOUT, ...fetchOptions } = options;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const { accessToken } = useAuthStore.getState();

      // Do not log JWT payloads; avoid token or claim leakage in console

      const hasExplicitBody = fetchOptions.body !== undefined;
      const headers = new Headers(fetchOptions.headers);

      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }

      if (hasExplicitBody && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }

      const response = await fetch(
        endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`,
        {
          ...fetchOptions,
          signal: controller.signal,
          headers,
        },
      );

      clearTimeout(timeoutId);

      // Handle 401 responses with token refresh (only on first attempt)
      if (response.status === 401 && accessToken && !isRetry) {
        const refreshed = await useAuthStore.getState().refreshTokens();
        if (refreshed) {
          // Retry request with new token (mark as retry to prevent infinite loop)
          return this.request(endpoint, options, true);
        } else {
          // Refresh failed - emit event for App component to handle navigation
          // This avoids full page reload and preserves React Router state
          const currentRoute = window.location.pathname + window.location.search;
          if (currentRoute !== '/login') {
            sessionStorage.setItem('intended_route', currentRoute);
          }
          useAuthStore.getState().clearAuth();

          // Emit custom event that App component listens for
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('auth:redirect-to-login', { detail: { from: currentRoute } }),
            );
          }

          throw new Error('Authentication required');
        }
      }

      if (!response.ok) {
        if (response.status === 429 && typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('api:rate-limited', {
              detail: {
                retryAfter: response.headers.get('retry-after') ?? null,
              },
            }),
          );
        }

        const errorData = await response.json().catch(() => null);

        const fallbackMessage = `HTTP ${response.status}: ${response.statusText}`;

        const normalizedMessage =
          (typeof errorData === 'string' && errorData) ||
          (errorData &&
            typeof (errorData as { message?: unknown }).message === 'string' &&
            (errorData as { message: string }).message) ||
          (errorData &&
            typeof (errorData as { error?: unknown }).error === 'string' &&
            (errorData as { error: string }).error) ||
          fallbackMessage;

        const normalizedCode =
          errorData &&
          typeof errorData === 'object' &&
          'code' in errorData &&
          typeof (errorData as { code?: unknown }).code === 'string'
            ? (errorData as { code: string }).code
            : undefined;

        const normalizedDetails =
          errorData && typeof errorData === 'object' && 'details' in errorData
            ? (errorData as { details?: unknown }).details
            : errorData && typeof errorData === 'object' && 'reason' in errorData
              ? (errorData as { reason?: unknown }).reason
              : errorData && typeof errorData === 'object'
                ? errorData
                : undefined;

        const error: ApiError = {
          message: normalizedMessage,
          code: normalizedCode,
          details: normalizedDetails,
          status: response.status,
        };
        throw error;
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      return response.text() as unknown as T;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Request timeout after ${timeout}ms`);
      }

      throw error;
    }
  }

  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' }, false);
  }

  /**
   * Fetch a page of data from a PostgREST endpoint along with the total row count.
   * Sends `Prefer: count=exact` so PostgREST includes a Content-Range header.
   */
  async getWithCount<T>(endpoint: string, options?: RequestOptions): Promise<{ data: T[]; count: number }> {
    const { timeout = API_CONFIG.TIMEOUT, ...fetchOptions } = options ?? {};
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const { accessToken } = useAuthStore.getState();
      const headers = new Headers(fetchOptions.headers);
      if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
      headers.set('Prefer', 'count=exact');

      const response = await fetch(
        endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`,
        { ...fetchOptions, method: 'GET', signal: controller.signal, headers },
      );
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const message = (errorData as { message?: string })?.message ?? `HTTP ${response.status}`;
        const error: ApiError = { message, status: response.status };
        throw error;
      }

      const contentRange = response.headers.get('content-range') ?? '';
      // PostgREST Content-Range format: "0-24/538" or "*/0" when empty
      const total = parseInt(contentRange.split('/')[1] ?? '0', 10) || 0;
      const data: T[] = await response.json();

      return { data, count: total };
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Fetch every row from a PostgREST endpoint using keyset pagination.
   * Appends `order=id.asc` (if no order is present) and pages via
   * `id=gt.{lastId}` instead of offset to avoid O(n²) sequential scans
   * on large tables (AB#29164).
   */
  async getAll<T>(endpoint: string, options?: RequestOptions, pageSize = 1000): Promise<T[]> {
    const all: T[] = [];
    let offset = 0;

    while (true) {
      const separator = endpoint.includes('?') ? '&' : '?';
      const pagedEndpoint = `${endpoint}${separator}limit=${pageSize}&offset=${offset}`;
      const page = await this.request<T[]>(pagedEndpoint, {
        ...options,
        method: 'GET',
      }, false);
      all.push(...page);
      if (page.length < pageSize) break;
      offset += pageSize;
    }
    return all;
  }

  async post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        ...options,
        method: 'POST',
        body: data === undefined ? undefined : JSON.stringify(data),
      },
      false,
    );
  }

  async patch<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        ...options,
        method: 'PATCH',
        body: data === undefined ? undefined : JSON.stringify(data),
      },
      false,
    );
  }

  async put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        ...options,
        method: 'PUT',
        body: data === undefined ? undefined : JSON.stringify(data),
      },
      false,
    );
  }

  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' }, false);
  }
}

// Singleton HTTP client instance
export const httpClient = new HttpClientImpl();

// Error handling utilities
export const isApiError = (error: unknown): error is ApiError => {
  return typeof error === 'object' && error !== null && 'message' in error && 'status' in error;
};

export const getErrorMessage = (error: unknown): string => {
  if (isApiError(error)) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
};
